import {
  AUTO_SCROLL_DIRECTION_FORWARD,
  REDUCED_MOTION_MEDIA_QUERY,
  elementEventListenerOptions,
} from "./constants";
import { createEventManager } from "./eventManager";
import { isDesktopViewport } from "./mediaQueries";

export const createAutoScrollController = ({ element, doc, win, config }) => {
  const events = createEventManager();
  const prefersReducedMotion = win.matchMedia(
    REDUCED_MOTION_MEDIA_QUERY
  ).matches;
  let autoScrollDirection = AUTO_SCROLL_DIRECTION_FORWARD;
  let autoScrollFrameId = null;
  let autoScrollIdleTimeoutId = null;
  let autoScrollLastTimestamp = null;
  let autoScrollPosition = element.scrollTop;

  const getMaxScrollTop = () => element.scrollHeight - element.clientHeight;

  const isTimelineTopInViewport = () => {
    const timelineBounds = element.getBoundingClientRect();
    const viewportHeight = win.innerHeight || doc.documentElement.clientHeight;

    return timelineBounds.top >= 0 && timelineBounds.top <= viewportHeight;
  };

  const clearAutoScrollIdleTimeout = () => {
    if (autoScrollIdleTimeoutId !== null) {
      win.clearTimeout(autoScrollIdleTimeoutId);
    }

    autoScrollIdleTimeoutId = null;
  };

  const stopAutoScroll = () => {
    if (autoScrollFrameId !== null) {
      win.cancelAnimationFrame(autoScrollFrameId);
    }

    autoScrollFrameId = null;
    autoScrollLastTimestamp = null;
  };

  const runAutoScrollFrame = (timestamp) => {
    if (!isDesktopViewport(win)) {
      stopAutoScroll();
      return;
    }

    const maxScrollTop = getMaxScrollTop();

    if (maxScrollTop <= 0) {
      stopAutoScroll();
      return;
    }

    if (autoScrollLastTimestamp === null) {
      autoScrollLastTimestamp = timestamp;
    }

    const elapsedTimeMs = timestamp - autoScrollLastTimestamp;
    const elapsedTimeSeconds = elapsedTimeMs / 1000;
    const distanceToScroll =
      config.speedPxPerSecond * elapsedTimeSeconds * autoScrollDirection;

    autoScrollLastTimestamp = timestamp;

    let nextScrollTop = autoScrollPosition + distanceToScroll;

    if (nextScrollTop >= maxScrollTop) {
      nextScrollTop = maxScrollTop;
      autoScrollPosition = nextScrollTop;
      element.scrollTop = nextScrollTop;
      stopAutoScroll();
      return;
    }

    autoScrollPosition = nextScrollTop;
    element.scrollTop = nextScrollTop;
    autoScrollFrameId = win.requestAnimationFrame(runAutoScrollFrame);
  };

  const startAutoScroll = () => {
    if (
      prefersReducedMotion ||
      autoScrollFrameId !== null ||
      getMaxScrollTop() <= 0
    ) {
      return;
    }

    autoScrollPosition = element.scrollTop;
    autoScrollFrameId = win.requestAnimationFrame(runAutoScrollFrame);
  };

  const scheduleAutoScroll = () => {
    if (prefersReducedMotion || !isDesktopViewport(win)) {
      return;
    }

    clearAutoScrollIdleTimeout();

    if (!isTimelineTopInViewport()) {
      return;
    }

    autoScrollIdleTimeoutId = win.setTimeout(() => {
      if (!isTimelineTopInViewport()) {
        return;
      }

      startAutoScroll();
    }, config.idleDelayMs);
  };

  const pauseAndRescheduleAutoScroll = () => {
    autoScrollPosition = element.scrollTop;
    stopAutoScroll();
    scheduleAutoScroll();
  };

  const syncAutoScrollWithViewport = () => {
    if (!isDesktopViewport(win)) {
      clearAutoScrollIdleTimeout();
      stopAutoScroll();
      return;
    }

    if (!isTimelineTopInViewport()) {
      clearAutoScrollIdleTimeout();
      stopAutoScroll();
      return;
    }

    scheduleAutoScroll();
  };

  events.add(
    element,
    "wheel",
    pauseAndRescheduleAutoScroll,
    elementEventListenerOptions.wheel
  );

  events.add(
    element,
    "touchstart",
    pauseAndRescheduleAutoScroll,
    elementEventListenerOptions.touchstart
  );

  events.add(element, "keydown", pauseAndRescheduleAutoScroll);
  events.add(win, "scroll", syncAutoScrollWithViewport, { passive: true });
  events.add(win, "resize", syncAutoScrollWithViewport);

  scheduleAutoScroll();

  return {
    pauseAndRescheduleAutoScroll,
    destroy: () => {
      clearAutoScrollIdleTimeout();
      stopAutoScroll();
      events.cleanup();
    },
  };
};
